import { motion } from "framer-motion";
import { Play, Pause, Lock, ShoppingCart, Download, CheckCircle2, Trash2, Loader2, Pencil, Link2, Heart, Headphones, Ban } from "lucide-react";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";
import { BeatCover } from "@/components/BeatCover";
import { Waveform, fmt } from "@/components/Waveform";

const shareBeat = async (b) => {
  const url = `${window.location.origin}/api/share/beat/${b.beat_id}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success(`Link to "${b.title}" copied`);
  } catch (e) {
    window.prompt("Copy this link:", url);
  }
};

const BeatTags = ({ b }) => {
  const tags = [b.bpm && `${b.bpm} BPM`, b.key, b.genre].filter(Boolean);
  if (!tags.length) return <p className="text-[11px] text-[#A0A0A0] uppercase tracking-wider">Full stems included</p>;
  return (
    <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`beat-tags-${b.beat_id}`}>
      {tags.map((t) => (
        <span key={t} className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border border-white/10 text-white/60">{t}</span>
      ))}
    </div>
  );
};

export const BeatRow = ({ b, index, user, buying, onBuy, onDelete, onStems, onEdit, onFavorite, highlighted }) => {
  const { track, playing: anyPlaying, time, dur, toggle, seek } = usePlayer();
  const isCurrent = track?.beat_id === b.beat_id;
  const playing = isCurrent && anyPlaying;
  const pct = isCurrent && dur ? (time / dur) * 100 : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      id={`beat-${b.beat_id}`}
      className={`group relative scroll-mt-28 px-4 md:px-5 py-3 border-b border-white/5 last:border-b-0 transition-[background-color,box-shadow] duration-700 ${highlighted ? "bg-[#D90429]/20 shadow-[inset_0_0_0_1px_#D90429]" : isCurrent ? "bg-[#1A0306]/70" : "hover:bg-white/[0.03]"}`}
      data-testid={`beat-card-${b.beat_id}`}>
      {isCurrent && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#D90429]" />}
      <div className="flex flex-wrap lg:flex-nowrap items-center gap-x-4 gap-y-3">

      <button onClick={() => toggle(b)} data-testid={`beat-play-${b.beat_id}`} aria-label={playing ? "Pause" : "Play"}
        className="relative h-12 w-12 shrink-0 overflow-hidden">
        <BeatCover beat={b} className="h-12 w-12" testId={`beat-cover-${b.beat_id}`} />
        <span className={`absolute inset-0 flex items-center justify-center transition-colors ${playing ? "bg-[#D90429]/80" : "bg-black/45 group-hover:bg-[#D90429]/70"}`}>
          {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4 ml-0.5" fill="currentColor" />}
        </span>
      </button>

      <div className="min-w-0 w-[calc(100%-4rem)] lg:w-60 shrink-0">
        <h3 className="font-display text-sm md:text-base font-bold leading-tight truncate" data-testid={`beat-title-${b.beat_id}`}>{b.title}</h3>
        <div className="flex flex-wrap items-center gap-1">
          <BeatTags b={b} />
          {user?.role === "admin" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 mt-0.5 bg-[#D90429]/15 text-[#ff6b7f]" data-testid={`beat-plays-${b.beat_id}`}>
              <Headphones className="h-3 w-3" /> {b.play_count || 0} {b.play_count === 1 ? "play" : "plays"}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-1 min-w-0 basis-full lg:basis-auto">
        <span className="text-[11px] font-mono text-white/50 w-9 text-right">{fmt(isCurrent ? time : 0)}</span>
        <Waveform pct={pct} active={playing} testId={`beat-progress-${b.beat_id}`}
          onSeek={(r) => (isCurrent ? seek(r) : toggle(b))} />
        <span className="text-[11px] font-mono text-white/50 w-9">{fmt(isCurrent ? dur : 0)}</span>
      </div>

      <div className="flex items-center justify-between lg:justify-start gap-3 lg:ml-auto shrink-0 basis-full lg:basis-auto">
        <span className={`font-display text-base md:text-lg font-bold whitespace-nowrap ${b.for_sale === false && b.status !== "sold" ? "text-white/20" : "text-[#D90429]"}`}>{b.for_sale === false && b.status !== "sold" ? "—" : `RON ${b.price}`}</span>
        <div className="flex items-center gap-2">
          <AdminIcon onClick={() => onFavorite(b)} testId={`beat-fav-${b.beat_id}`} title={b.favorited ? "Remove from favourites" : "Add to favourites"}>
            <Heart className={`h-4 w-4 transition-transform ${b.favorited ? "text-[#D90429] scale-110" : ""}`} fill={b.favorited ? "currentColor" : "none"} />
          </AdminIcon>
          <AdminIcon onClick={() => shareBeat(b)} testId={`beat-share-${b.beat_id}`} title="Copy share link"><Link2 className="h-4 w-4" /></AdminIcon>
          <BeatAction b={b} user={user} buying={buying} onBuy={onBuy} onStems={onStems} />
          {user?.role === "admin" && (
            <>
              <AdminIcon onClick={() => onEdit(b)} testId={`beat-edit-${b.beat_id}`} title="Edit beat"><Pencil className="h-4 w-4" /></AdminIcon>
              <AdminIcon onClick={() => onDelete(b)} testId={`beat-delete-${b.beat_id}`} title="Delete beat"><Trash2 className="h-4 w-4" /></AdminIcon>
            </>
          )}
        </div>
      </div>
      </div>

      {user?.role === "admin" && b.status === "sold" && (
        <p className="w-full text-xs text-[#A0A0A0] mt-2 md:pl-16" data-testid={`beat-buyer-${b.beat_id}`}>Buyer: {b.buyer_name} ({b.buyer_email})</p>
      )}
    </motion.div>
  );
};

const AdminIcon = ({ onClick, testId, title, children }) => (
  <button onClick={onClick} data-testid={testId} title={title}
    className="h-8 w-8 flex items-center justify-center border border-white/15 text-white/50 hover:border-[#D90429] hover:text-[#D90429] transition-colors">
    {children}
  </button>
);

const BeatAction = ({ b, user, buying, onBuy, onStems }) => {
  if (b.owned) return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center gap-1 text-xs text-white/70"><CheckCircle2 className="h-4 w-4 text-[#D90429]" /> Owned</span>
      {b.has_stems && (
        <button onClick={() => onStems(b)} data-testid={`beat-stems-${b.beat_id}`}
          className="inline-flex items-center gap-1 border border-white/20 hover:border-white text-white px-3 py-1.5 text-xs font-bold uppercase">
          <Download className="h-4 w-4" /> Stems
        </button>
      )}
    </div>
  );
  if (b.status === "sold") return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 bg-white/10 text-white/50" data-testid={`beat-sold-${b.beat_id}`}>Sold</span>
  );
  if (b.for_sale === false) return (
    <span className="relative group/nfs" data-testid={`beat-nfs-${b.beat_id}`}>
      <button type="button" disabled aria-label="Not for sale"
        className="inline-flex items-center gap-2 border border-white/15 text-white/40 px-4 py-2 text-xs font-bold uppercase tracking-wide cursor-not-allowed">
        <Ban className="h-4 w-4" /> NFS
      </button>
      <span role="tooltip" className="pointer-events-none absolute bottom-full right-0 mb-2 whitespace-nowrap bg-white text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1 opacity-0 translate-y-1 group-hover/nfs:opacity-100 group-hover/nfs:translate-y-0 transition-[opacity,transform]">
        Not for sale
      </span>
    </span>
  );
  return (
    <button onClick={() => onBuy(b.beat_id)} disabled={buying === b.beat_id} data-testid={`beat-buy-${b.beat_id}`}
      className="inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#ED2939] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-transform hover:-translate-y-0.5">
      {buying === b.beat_id ? <Loader2 className="h-4 w-4 animate-spin" /> : !user ? <Lock className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
      Buy
    </button>
  );
};
